// src/lib/message-segments.js — Lot 0 de l'éditeur de templates segmenté.
//
// Module PUR (0 I/O). Convertit le body/subject stocké — une string qui mêle
// texte, variables `{name}` et blocs conditionnels `{#if name}…{/if}` — en un
// ARBRE de segments manipulable par l'UI future (Option C, décision Dimitri),
// et inversement. Le MOTEUR de rendu (src/lib/template-variables.js,
// lib/messages-engine.js) N'EST PAS touché : la string reste la source de
// vérité stockée. Ce module n'est que la couche parse/serialize.
//
// ── COUPLAGE MOTEUR (src/lib/template-variables.js) — reproduit à l'identique ──
// Pour garantir la MÊME tokenisation que le rendu, on reproduit les regex du
// résolveur (elles ne sont pas exportées côté moteur) :
//   IF_BLOCK_RE = /([ \t]*)\{#if\s+([a-zA-Z_][a-zA-Z0-9_ \t]*)\}([\s\S]*?)\{\/if\}([ \t]*)(\r?\n)?/g
//   VAR_RE      = /\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g
// → nom de variable = `[a-zA-Z_][a-zA-Z0-9_]*` (VAR_RE, inchangé).
// → l'EXPRESSION d'un `{#if}` peut, côté MOTEUR, contenir ESPACES et
//   TABULATIONS (`[a-zA-Z0-9_ \t]*`) : c'est le CUMUL de conditions
//   `{#if A ET B}` / `{#if A OU B}` (cf. evalIfExpression, twin CJS
//   lib/template-variables.js).
//   ⚠️ Côté ÉDITEUR, cette reconnaissance est OPT-IN (`{ allowCumul: true }`,
//   B4-ext). Elle est posée par le CORPS des deux éditeurs (Direct et
//   Plateformes) ; par DÉFAUT (`false`), la classe reste l'HISTORIQUE
//   `[a-zA-Z_][a-zA-Z0-9_]*`. Cf. `ifOpenSrc` plus bas.
// → l'ouverture d'un if accepte `\s+` entre `#if` et l'expression ; la fermeture
//   est le littéral EXACT `{/if}` (aucun espace).
// Toute évolution des regex moteur doit être répercutée ici (ADR-021 esprit).
//
// ── Sémantique du cumul (RÉPLIQUE de evalIfExpression, jamais évaluée ici) ──
// Jetons séparés par des espaces : positions PAIRES = opérandes (noms de
// variables), positions IMPAIRES = opérateurs `ET` / `OU` en MAJUSCULES
// STRICTES. Un seul type d'opérateur par bloc. Toute autre forme (arité paire,
// mélange ET/OU, opérateur inconnu, expression vide) est NON SUPPORTÉE : le
// moteur MASQUE alors le bloc (fail-safe). `parseIfExpression` le signale par
// `valid:false` — l'éditeur doit le DIRE à l'admin, jamais « réparer »
// l'expression tout seul.
//
// ── Décisions de parsing (Dimitri) ──
// • Segments en ORDRE LIBRE : un `if` peut être n'importe où (tête, queue,
//   adjacent à un autre `if` ou à une var). Le parse est un scan gauche→droite.
// • Accolades orphelines / faux tokens : `{`, `}`, `{ pas une var }` (espaces),
//   `{#if}` (sans nom) ne matchent AUCUNE regex → conservés VERBATIM en texte,
//   exactement comme le moteur les laisse littéraux. Aucune normalisation.
// • Whitespace autour des balises : les nœuds `text` préservent les slices
//   exactes (emojis, retours ligne, espaces de bord inclus). L'ouverture d'un
//   `if` est stockée VERBATIM (`node.open`) → round-trip byte-identique même si
//   l'ouverture contient un whitespace non canonique (`{#if  x}`).
// • NESTING interdit : le moteur (IF_BLOCK_RE non récursive) ne sait pas
//   imbriquer un `if` dans un `if`. Un arbre de segments dont un `if` contient
//   un `if` n'est donc pas un modèle valide → `parseSegments` REJETTE (throw)
//   les `{#if}` imbriqués, les `{/if}` orphelins et les `{#if}` non fermés.
//   Ces contenus deviennent NON round-trippables → l'éditeur les ouvrira en
//   LECTURE SEULE (comportement UI câblé au Lot 1 ; ici on fournit le prédicat
//   `isRoundTrippable`).

/** Erreur de structure : contenu non modélisable en arbre de segments propre. */
export class SegmentParseError extends Error {
  constructor(message, code) {
    super(message);
    this.name = "SegmentParseError";
    this.code = code; // 'NESTED_IF' | 'ORPHAN_CLOSE' | 'UNCLOSED_IF'
  }
}

// Sous-sources des tokens, alignées mot pour mot sur les regex moteur.
//
// ── DEUX MODES DE TOKENISATION (B4-ext) ────────────────────────────────────
// • `allowCumul:false` (DÉFAUT) — classe HISTORIQUE `[a-zA-Z_][a-zA-Z0-9_]*` :
//   seule une condition SIMPLE `{#if x}` est une ouverture. `{#if a ET b}` reste
//   du TEXTE littéral → son `{/if}` lève ORPHAN_CLOSE → champ en LECTURE SEULE.
//   C'est le comportement EXACT d'avant B4-ext. Garder ce défaut BORNE le
//   blast-radius : ouvrir un champ au cumul reste une décision EXPLICITE.
// • `allowCumul:true` (OPT-IN) — classe du MOTEUR `[a-zA-Z_][a-zA-Z0-9_ \t]*`,
//   copie stricte du g2 de IF_BLOCK_RE (src/lib/template-variables.js + twin CJS
//   lib/template-variables.js, ADR-021) : le CUMUL `{#if A ET B}` /
//   `{#if A OU B}` devient une ouverture éditable, exactement comme le moteur le
//   voit déjà. Posé par le CORPS des deux éditeurs (Direct et Plateformes) —
//   décision Dimitri 2026-07-28 : le message de PROD `demande_caution_formulaire`
//   contient 3 cumuls et devenait NON MODIFIABLE sans cette option.
//
// ⚠️ En mode cumul, toute divergence d'un caractère avec la regex moteur ferait
// voir à l'éditeur un bloc que le rendu ne voit pas (ou l'inverse) → corruption
// silencieuse. Le test de PARITÉ (message-segments.cumul.test.js) verrouille ça.
// ⚠️ En mode HISTORIQUE, l'éditeur est volontairement PLUS STRICT que le moteur :
// il refuse d'éditer (lecture seule) un bloc que le moteur sait rendre. C'est
// fail-safe (aucune écriture possible), jamais l'inverse.
const IF_EXPR_SINGLE_SRC = "[a-zA-Z_][a-zA-Z0-9_]*";
const IF_EXPR_CUMUL_SRC = "[a-zA-Z_][a-zA-Z0-9_ \\t]*";
const ifOpenSrc = (allowCumul) =>
  `\\{#if\\s+(${allowCumul ? IF_EXPR_CUMUL_SRC : IF_EXPR_SINGLE_SRC})\\}`;
const IF_CLOSE_SRC = "\\{\\/if\\}";
const VAR_SRC = "\\{([a-zA-Z_][a-zA-Z0-9_]*)\\}";

// Opérateurs de cumul reconnus (MAJUSCULES strictes — cf. moteur).
const CUMUL_OPERATORS = ["ET", "OU"];

/**
 * Analyse l'EXPRESSION d'un bloc `{#if EXPR}` — fonction PURE, aucune
 * évaluation : elle réplique STRICTEMENT les règles d'acceptation de
 * `evalIfExpression` (moteur) pour que l'éditeur dise la vérité du rendu.
 *
 *   1 jeton          → { operands:[t], operator:null, valid:true }  (single-var)
 *   n jetons impairs, opérateurs HOMOGÈNES ET/OU
 *                    → { operands:[…], operator:'ET'|'OU', valid:true }
 *   0 jeton, arité paire, mélange ET/OU, opérateur inconnu
 *                    → valid:false (le moteur MASQUE le bloc — fail-safe)
 *
 * @param {string|null|undefined} expr
 * @returns {{operands:string[], operator:('ET'|'OU'|null), valid:boolean}}
 */
export function parseIfExpression(expr) {
  const tokens =
    expr == null ? [] : String(expr).trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0)
    return { operands: [], operator: null, valid: false };
  if (tokens.length === 1)
    return { operands: [tokens[0]], operator: null, valid: true };
  if (tokens.length % 2 === 0)
    return { operands: tokens, operator: null, valid: false };

  const operands = [];
  const ops = [];
  for (let i = 0; i < tokens.length; i++) {
    (i % 2 === 0 ? operands : ops).push(tokens[i]);
  }
  const allEt = ops.every((o) => o === "ET");
  const allOu = ops.every((o) => o === "OU");
  if (!allEt && !allOu) return { operands, operator: null, valid: false };
  return { operands, operator: allEt ? "ET" : "OU", valid: true };
}

/**
 * Forme CANONIQUE d'une expression de cumul — la SEULE que l'éditeur émette
 * quand l'utilisateur modifie explicitement une condition (`a ET b`).
 * @param {string[]} operands
 * @param {'ET'|'OU'|null} operator
 * @returns {string}
 */
export function formatIfExpression(operands, operator) {
  const list = (operands || []).filter(Boolean);
  if (list.length <= 1) return list[0] || "";
  const op = CUMUL_OPERATORS.includes(operator) ? operator : "ET";
  return list.join(` ${op} `);
}

// Scanner unique, alternation gauche→droite : ouverture-if, fermeture-if, var.
// Aucune collision possible : `#` et `/` ne sont pas des `\w`, donc une var ne
// peut jamais matcher un token `if` et réciproquement → l'ordre des branches
// est neutre. Une nouvelle instance est créée à chaque parse (état `lastIndex`
// isolé, module réentrant / thread-safe côté JS).
function makeTokenRe(allowCumul) {
  return new RegExp(`${ifOpenSrc(allowCumul)}|${IF_CLOSE_SRC}|${VAR_SRC}`, "g");
}

/**
 * Parse une string en arbre ordonné de segments.
 * @param {string|null|undefined} str
 * @param {{allowCumul?: boolean}} [options] - `allowCumul:true` reconnaît le
 *   cumul `{#if A ET B}` comme une ouverture (posé par le CORPS des éditeurs
 *   Direct et Plateformes). DÉFAUT `false` = tokenisation HISTORIQUE,
 *   byte-pour-byte comme avant B4-ext.
 * NB : sur un nœud `if`, `name` est la CAPTURE BRUTE de l'expression — un nom
 * de variable pour une condition simple, une expression de cumul (« a ET b »)
 * sinon. La forme du nœud est INCHANGÉE (aucun champ dérivé n'est ajouté) :
 * l'appelant qui a besoin des opérandes appelle `parseIfExpression(node.name)`.
 * @returns {Array<{type:'text',value:string}
 *                 |{type:'var',name:string}
 *                 |{type:'if',name:string,children:Array,open:string}>}
 * @throws {SegmentParseError} sur if imbriqué / {/if} orphelin / {#if} non fermé.
 */
export function parseSegments(str, options) {
  const allowCumul = !!(options && options.allowCumul);
  if (str == null) return [];
  const s = String(str);
  const root = [];
  // stack = pile des listes d'enfants courantes. Comme le nesting est interdit,
  // sa profondeur ne dépasse jamais 2 ([root] ou [root, children d'un if]).
  const stack = [root];
  const current = () => stack[stack.length - 1];
  const pushText = (from, to) => {
    if (to > from) current().push({ type: "text", value: s.slice(from, to) });
  };

  const re = makeTokenRe(allowCumul);
  let lastIndex = 0;
  let m;
  while ((m = re.exec(s)) !== null) {
    pushText(lastIndex, m.index);
    lastIndex = re.lastIndex;
    const full = m[0];
    const ifOpenName = m[1];
    const varName = m[2];

    if (ifOpenName !== undefined) {
      // Ouverture d'un bloc conditionnel.
      if (stack.length > 1) {
        throw new SegmentParseError(
          `if imbriqué non supporté (moteur non récursif) : {#if ${ifOpenName}} à l'index ${m.index}`,
          "NESTED_IF",
        );
      }
      const node = { type: "if", name: ifOpenName, children: [], open: full };
      current().push(node);
      stack.push(node.children);
    } else if (varName !== undefined) {
      current().push({ type: "var", name: varName });
    } else {
      // Fermeture {/if}.
      if (stack.length <= 1) {
        throw new SegmentParseError(
          `balise {/if} orpheline à l'index ${m.index}`,
          "ORPHAN_CLOSE",
        );
      }
      stack.pop();
    }
  }
  pushText(lastIndex, s.length);

  if (stack.length > 1) {
    throw new SegmentParseError(
      "balise {#if} non fermée en fin de contenu",
      "UNCLOSED_IF",
    );
  }
  return root;
}

/**
 * Reconstruit la string depuis un arbre de segments.
 * INVARIANT ABSOLU : serializeSegments(parseSegments(x)) === x (byte-identique)
 * pour toute string que parseSegments accepte. L'ouverture d'un `if` réutilise
 * `node.open` (verbatim d'origine) si présent, sinon la forme canonique.
 * @param {Array} tree
 * @returns {string}
 */
export function serializeSegments(tree) {
  if (!Array.isArray(tree)) return "";
  let out = "";
  for (const node of tree) {
    if (!node || typeof node !== "object") continue;
    if (node.type === "text") {
      out += node.value != null ? String(node.value) : "";
    } else if (node.type === "var") {
      out += "{" + node.name + "}";
    } else if (node.type === "if") {
      out += node.open != null ? node.open : "{#if " + node.name + "}";
      out += serializeSegments(node.children);
      out += "{/if}";
    }
  }
  return out;
}

/**
 * Prédicat de sûreté : `true` si le contenu peut être parsé PUIS re-sérialisé à
 * l'octet près. `false` = l'éditeur devra refuser d'ÉDITER (ouverture en lecture
 * seule) pour ne jamais corrompre silencieusement (nesting, balises orphelines).
 * @param {string|null|undefined} str
 * @param {{allowCumul?: boolean}} [options] - cf. `parseSegments`. DÉFAUT
 *   `false` : un cumul `{#if A ET B}` reste NON round-trippable (lecture seule),
 *   exactement comme avant B4-ext.
 * @returns {boolean}
 */
export function isRoundTrippable(str, options) {
  if (str == null) return true; // rien à éditer → trivialement sûr (arbre vide)
  const s = String(str);
  try {
    return serializeSegments(parseSegments(s, options)) === s;
  } catch (_) {
    return false;
  }
}

// ── Signature structurelle — JUMEAU CLIENT de lib/ai-translate.js ───────────
//
// ⚠️ POURQUOI CE HELPER EXISTE (leçon B4-ext, blocker QA n°1).
// `isRoundTrippable` ne compare RIEN : elle dit seulement « cette string se
// re-sérialise à l'octet près ». Tant que le cumul n'était pas reconnu, elle
// servait de filet PAR ACCIDENT — une traduction contenant `{#if a ET b}`
// tombait en `{/if}` orphelin, donc était rejetée. Dès qu'on reconnaît le
// cumul, `{#if a AND b}` (opérateur traduit par l'IA) devient parfaitement
// round-trippable → le filet DISPARAÎT en silence, et un paragraphe
// conditionnel peut s'évaporer du mail EN/ES sans que personne ne le voie.
//
// Le vrai contrôle est donc une COMPARAISON FR ↔ traduction, alignée sur
// `_structuralSignature` / `_sameStructure` de lib/ai-translate.js (garde
// serveur). Les deux doivent rester d'accord : même ensemble de `{var}`, mêmes
// EXPRESSIONS `{#if …}` (normalisées), même comptage de blocs.
const _WS_RE = /\s+/g;

/**
 * Signature structurelle d'un contenu : `null` si la string n'est pas parsable
 * (l'appelant traite ça comme un mismatch — jamais comme « équivalent »).
 * @param {string|null|undefined} str
 * @param {{allowCumul?: boolean}} [options] - cf. `parseSegments`.
 * @returns {{vars:string, ifs:string, ifCount:number}|null}
 */
export function structuralSignature(str, options) {
  let tree;
  try {
    tree = parseSegments(str, options);
  } catch (_) {
    return null;
  }
  const vars = new Set();
  const ifs = [];
  const walk = (nodes) => {
    for (const n of nodes) {
      if (!n || typeof n !== "object") continue;
      if (n.type === "var") vars.add(n.name);
      else if (n.type === "if") {
        // Forme CANONIQUE quand l'expression est évaluable par le moteur
        // (« a  ET   b » ≡ « a ET b ») ; sinon, repli sur l'expression brute
        // normalisée — on ne « répare » jamais, on compare tel quel.
        const p = parseIfExpression(n.name);
        ifs.push(
          p.valid
            ? formatIfExpression(p.operands, p.operator)
            : String(n.name == null ? "" : n.name)
                .trim()
                .replace(_WS_RE, " "),
        );
        walk(n.children);
      }
    }
  };
  walk(tree);
  return {
    vars: Array.from(vars).sort().join(","),
    // Trié : un réordonnancement légitime des blocs par l'IA reste accepté.
    ifs: ifs.slice().sort().join("|"),
    ifCount: ifs.length,
  };
}

/**
 * `true` si deux contenus portent EXACTEMENT la même structure moteur.
 * Une string non parsable d'un côté ⇒ `false` (fail-safe).
 * @param {string|null|undefined} a
 * @param {string|null|undefined} b
 * @param {{allowCumul?: boolean}} [options] - cf. `parseSegments`.
 * @returns {boolean}
 */
export function sameStructure(a, b, options) {
  const sa = structuralSignature(a, options);
  const sb = structuralSignature(b, options);
  if (!sa || !sb) return false;
  return sa.vars === sb.vars && sa.ifs === sb.ifs && sa.ifCount === sb.ifCount;
}
